import { Alert, SpinnerOverlay } from '@quality24/design-system';
import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { LoginFormValues } from '../../components/organisms/LoginForm';
import { NewPasswordFormValues } from '../../components/organisms/NewPasswordForm';
import { IsFederatedUserPayload } from '../../contexts/auth/authContext';
import { useAuth } from '../../contexts/auth/useAuth';
import {
  getClientId,
  getRedirectUri,
  parseQuery,
  stringifyQuery,
} from '../../utils/url';
import NewPasswordSection from './NewPasswordSection';
import SignInSection from './SignInSection';

const LoginPage: React.FunctionComponent = () => {
  const [userFederations, setUserFederations] = React.useState<
    IsFederatedUserPayload[] | null
  >(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [, setUrlSearchParams] = useSearchParams();

  const {
    route,
    inProgress,
    error,
    signIn,
    completeNewPassword,
    setRoute,
    getUserFederations,
  } = useAuth();

  // Get the query string object
  const query = parseQuery(location.search);
  const isFirstAccess =
    query?.firstAccess !== null && query?.firstAccess !== undefined;

  const initialValues: LoginFormValues = {
    email: (query.username as string) || '',
    password: '',
  };

  // Set initial page route
  React.useEffect(() => {
    if (!route) {
      setRoute('signIn');
    }
  }, [setRoute, route]);

  /** Set URL query params in case of redirect after federated logout */
  React.useEffect(() => {
    if (!query.clientId && !query.redirect_uri) {
      const searchParams = new URLSearchParams(location.search);

      // Merge query params
      Object.entries({
        clientId: getClientId(location.search),
        redirect_uri: getRedirectUri(location.search),
      }).forEach(([key, value]) => {
        searchParams.set(key, value || '');
      });

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      );
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    query.clientId,
    query.redirect_uri,
  ]);

  /**
   * Signs in the user
   */
  const handleLogin = React.useCallback(
    async ({ email, password }: LoginFormValues) => {
      try {
        await signIn(email, password);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to login: ', err);
      }
    },
    [signIn],
  );

  /**
   * Changes user password
   */
  const handleChangePassword = React.useCallback(
    async ({ newPassword }: NewPasswordFormValues) => {
      try {
        await completeNewPassword(newPassword);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to set new password: ', err);
      }
    },
    [completeNewPassword],
  );

  const handleOnSubmitEmail = React.useCallback(
    async (email: string, callback?: () => void) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      try {
        if (emailRegex.test(email)) {
          const response = await getUserFederations(email);
          setUrlSearchParams((prev) => {
            prev.set('username', email);
            return prev;
          });
          setUserFederations(response);
          callback?.();
        } else {
          setUserFederations(null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch user federations', err);
      }
    },
    [getUserFederations, setUrlSearchParams],
  );

  return (
    <>
      {error && (
        <Alert className="mb-3" variant="danger">
          {error}
        </Alert>
      )}
      {inProgress && <SpinnerOverlay size="sm" />}

      {route === 'signIn' && (
        <SignInSection
          isFirstAccess={isFirstAccess}
          initialValues={initialValues}
          inProgress={inProgress}
          onLogin={handleLogin}
          onUpdatePassword={(username) => {
            const search = username ? { ...query, username } : query;
            setRoute('resetPassword');
            navigate(`/forgot-password?${stringifyQuery(search)}`);
          }}
          onSubmitEmail={handleOnSubmitEmail}
          userFederations={
            userFederations?.filter((fed) => !!fed.enabled) || null
          }
        />
      )}

      {route === 'forceNewPassword' && (
        <NewPasswordSection
          inProgress={inProgress}
          onPasswordChange={handleChangePassword}
        />
      )}
    </>
  );
};

export default LoginPage;
